// paymentPlanForm — DEPENDENCY-FREE pure helpers for the standalone Security
// Deposit Payment Plan agreement (WEL-77). No network / no `@/` imports so it
// stays unit-testable in isolation (mirrors lib/moveoutFundsForm.ts).
//
// The schedule math here is a VERBATIM port of the backend engine so the
// client-side preview equals what gets sent:
//   - computePaymentScheduleCents ≡ apps/api/src/lib/leaseConfig.ts
//     computePaymentSchedule (cents-exact: floor(total/n) each, remainder in
//     the LAST payment).
//   - addMonths / ymd ≡ apps/api/src/lib/date.ts (local-time, day-of-month
//     clamp to the last valid day of the target month).
//   - validation ≡ apps/api/src/lib/paymentPlanDoc.ts validatePaymentPlanInput
//     + the 12-month max-span rule (paymentPlanSpanMonths).
//
// Imported by lib/paymentPlanClient.ts (re-exports for app call sites) and by
// PaymentPlanPage for the live schedule preview + Send-button gating.

// ---------------------------------------------------------------------------
// Constants — mirror apps/api/src/lib/leaseConfig.ts.
// ---------------------------------------------------------------------------
export const PAYMENT_PLAN_MIN_PAYMENTS = 2;
export const PAYMENT_PLAN_MAX_PAYMENTS = 6;
export const PAYMENT_PLAN_DEFAULT_INTERVAL_MONTHS = 1;
export const PAYMENT_PLAN_MAX_SPAN_MONTHS = 12;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const YMD_RE = /^\d{4}-\d{2}-\d{2}$/;

// ---------------------------------------------------------------------------
// Date helpers — VERBATIM port of apps/api/src/lib/date.ts (local-time, clamp).
// Kept here (not imported from lib/date.ts) so this module stays dependency-free
// and the ported logic is pinned to the engine it must match.
// ---------------------------------------------------------------------------

/** Format a Date as yyyy-mm-dd using LOCAL components (matches date.ts ymd). */
function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Add `n` calendar months to a yyyy-mm-dd date, clamping the day to the last
 * valid day of the target month. VERBATIM port of date.ts addMonths.
 *
 * @example
 *   addMonths('2026-01-31', 1)  === '2026-02-28'
 *   addMonths('2026-01-08', 12) === '2027-01-08'
 */
export function addMonths(ymdStr: string, n: number): string {
  const d = new Date(ymdStr + 'T00:00:00');
  const targetYear = d.getFullYear();
  const targetMonth = d.getMonth() + n;
  const clamped = new Date(targetYear, targetMonth + 1, 0);
  const originalDay = d.getDate();
  const lastDayOfTarget = clamped.getDate();
  const resultDay = Math.min(originalDay, lastDayOfTarget);
  return ymd(new Date(targetYear, targetMonth, resultDay));
}

// ---------------------------------------------------------------------------
// Schedule math — VERBATIM port of computePaymentSchedule (leaseConfig.ts).
// ---------------------------------------------------------------------------

/** One scheduled charge — mirrors backend ComputedPayment. */
export interface ScheduledPayment {
  /** 1-based payment number. */
  paymentNumber: number;
  /** yyyy-mm-dd — rendered as "Charge Date" in the schedule table. */
  chargeDate: string;
  /** Integer cents. */
  amount: number;
}

export interface ComputeScheduleArgs {
  /** Principal financed, in integer CENTS. */
  principalCents: number;
  /** Number of equal monthly payments (remainder lands in the last). */
  numberOfPayments: number;
  /** First charge date, yyyy-mm-dd. */
  firstChargeDate: string;
  /** Months between charges. Default 1 (monthly). */
  intervalMonths?: number;
}

/**
 * Compute the payment schedule, cents-exact, with the remainder in the LAST
 * payment. Byte-identical to the backend buildPaymentPlanSchedule →
 * computePaymentSchedule so the preview equals what's sent.
 *
 * The first (n - 1) payments each get floor(principal / n); the last absorbs
 * the remainder so sum(amounts) === principalCents exactly. Charge dates step
 * `intervalMonths` months from `firstChargeDate` (day-of-month clamp).
 */
export function computePaymentScheduleCents(args: ComputeScheduleArgs): ScheduledPayment[] {
  const n = args.numberOfPayments;
  const interval = args.intervalMonths ?? PAYMENT_PLAN_DEFAULT_INTERVAL_MONTHS;
  // Guard against a degenerate n so the preview never throws while the operator
  // is mid-edit (validate*() gates the actual send).
  if (!Number.isInteger(n) || n < 1) return [];
  const total = Math.max(0, Math.trunc(args.principalCents));
  const base = Math.floor(total / n);
  const remainder = total - base * n;
  return Array.from({ length: n }, (_, i) => ({
    paymentNumber: i + 1,
    chargeDate: addMonths(args.firstChargeDate, interval * i),
    amount: i === n - 1 ? base + remainder : base,
  }));
}

/** Months from the first charge to the last (mirrors paymentPlanSpanMonths). */
export function paymentPlanSpanMonths(numberOfPayments: number, intervalMonths: number): number {
  return Math.max(0, numberOfPayments - 1) * intervalMonths;
}

// ---------------------------------------------------------------------------
// Operator-entered plan input + validation — mirrors paymentPlanDoc.ts
// validatePaymentPlanInput.
// ---------------------------------------------------------------------------

/**
 * TYPE-ADAPTIVE principal: what the plan finances. Mirrors the backend
 * PaymentPlanPrincipalType (apps/api/src/lib/paymentPlanDoc.ts). Drives the
 * agreement's title, in-body labels, and closing clause.
 */
export type PaymentPlanPrincipalType =
  | 'security_deposit'
  | 'rent'
  | 'move_in_charges'
  | 'other';

export interface PaymentPlanInput {
  /** Principal financed, in integer CENTS. */
  principalCents: number;
  /** Number of equal monthly payments. */
  numberOfPayments: number;
  /** First charge date, yyyy-mm-dd. */
  firstChargeDate: string;
  /** Months between charges. Default 1. */
  intervalMonths?: number;
  /** What the plan finances. Defaults to 'security_deposit' when omitted. */
  principalType?: PaymentPlanPrincipalType;
  /** Freeform label used ONLY when principalType is 'other'. */
  otherLabel?: string;
}

/**
 * Validate the operator's plan numbers → a friendly error string, or null when
 * OK. Mirrors the backend so the UI can gate Send before the round-trip.
 */
export function validatePaymentPlanInput(input: PaymentPlanInput): string | null {
  if (!Number.isInteger(input.principalCents) || input.principalCents <= 0) {
    return 'Enter a principal amount greater than $0.';
  }
  if (
    !Number.isInteger(input.numberOfPayments) ||
    input.numberOfPayments < PAYMENT_PLAN_MIN_PAYMENTS ||
    input.numberOfPayments > PAYMENT_PLAN_MAX_PAYMENTS
  ) {
    return `Number of payments must be between ${PAYMENT_PLAN_MIN_PAYMENTS} and ${PAYMENT_PLAN_MAX_PAYMENTS}.`;
  }
  if (!YMD_RE.test(input.firstChargeDate)) {
    return 'Enter a valid start date.';
  }
  const interval = input.intervalMonths ?? PAYMENT_PLAN_DEFAULT_INTERVAL_MONTHS;
  if (!Number.isInteger(interval) || interval < 1) {
    return 'The interval between payments must be at least 1 month.';
  }
  if (paymentPlanSpanMonths(input.numberOfPayments, interval) > PAYMENT_PLAN_MAX_SPAN_MONTHS) {
    return `The plan can't run longer than ${PAYMENT_PLAN_MAX_SPAN_MONTHS} months.`;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Resident roster — the financially-responsible residents who'll sign.
// ---------------------------------------------------------------------------

/** A roster row the staff edit before sending (name + email validated). */
export interface PaymentPlanResidentDraft {
  tenantId: string;
  firstName: string;
  lastName: string;
  email: string;
}

/** A single resident row valid enough to receive the signing request. */
export function isResidentRowValid(r: PaymentPlanResidentDraft): boolean {
  return !!r.firstName.trim() && EMAIL_RE.test((r.email ?? '').trim());
}

/** True when an email is present but malformed (for the inline field error). */
export function isResidentEmailInvalid(r: PaymentPlanResidentDraft): boolean {
  const email = (r.email ?? '').trim();
  return !!email && !EMAIL_RE.test(email);
}

export interface PaymentPlanFormDraft {
  propertyId: string;
  unitId: string;
  residents: PaymentPlanResidentDraft[];
  input: PaymentPlanInput;
}

/**
 * True when the whole form is complete + valid enough to send: a property +
 * unit are picked, at least one resident has a name + valid email, no row has a
 * malformed email, and the plan numbers validate.
 */
export function canSendPaymentPlan(d: PaymentPlanFormDraft): boolean {
  if (!d.propertyId || !d.unitId) return false;
  if (validatePaymentPlanInput(d.input) !== null) return false;
  // TYPE-ADAPTIVE: the 'other' principal needs a non-empty freeform label (it
  // becomes "<label> Payment Plan"). All other types need nothing extra.
  if (d.input.principalType === 'other' && !(d.input.otherLabel ?? '').trim()) {
    return false;
  }
  const valid = d.residents.filter(isResidentRowValid);
  if (valid.length === 0) return false;
  if (d.residents.some(isResidentEmailInvalid)) return false;
  return true;
}
